import {
  HsaAutocheck,
  HsaIntentSignalSettings,
  resolveHsaIntentSignalSettings,
} from "@redotech/redo-conversion-core-common/conversion/hsa-autocheck";
import { HsaSessionStatus } from "@redotech/redo-conversion-hsa-common/rpc-schemas/types";
import {
  buildHsaIntentCartAttributes,
  HsaIntentSource,
  REDO_HSA_INTENT_ATTRIBUTE_KEY,
  REDO_HSA_PAYMENT_EXCLUSIVITY_ATTRIBUTE_KEY,
  REDO_HSA_PAYMENT_PRIORITY_ATTRIBUTE_KEY,
  safeParseHsaIntentCartAttribute,
} from "@redotech/redo-storefront-common/cart-attributes";
import { HsaSessionDriver } from "@redotech/redo-web-conversion/conversion/hsa/session-driver";
import { ExtensionShopifyClient } from "@redotech/shopify-client/ajax";
import { log } from "../monitoring/log";
import { datadogLog } from "../monitoring/monitor";
import { reportHsaIntentSignal } from "./intent-analytics";

/** The cart operations a signal needs; narrow so tests can pass a plain fake. */
export type HsaIntentCartClient = Pick<
  ExtensionShopifyClient,
  "cartGet" | "updateCartAttribute"
>;

const INTENT_SIGNAL_SETTING: Record<
  Exclude<HsaIntentSource, "buy_button">,
  keyof HsaIntentSignalSettings
> = {
  lmn_started: "lmnStarted",
  lmn_completed: "lmnCompleted",
  pdp_widget: "pdpWidget",
  cart_widget: "cartWidget",
};

/**
 * Whether the merchant's settings let this element's interaction record a
 * signal. The buy button always records; the other elements are governed by
 * `intentSignals`. Defaults are resolved again here because a cached payload
 * saved before the switches existed carries no `intentSignals`.
 */
export function isHsaIntentSignalEnabled(
  hsa: HsaAutocheck,
  source: HsaIntentSource,
): boolean {
  if (!hsa.enabled) {
    return false;
  }
  if (source === "buy_button") {
    return true;
  }
  return resolveHsaIntentSignalSettings(hsa.intentSignals)[
    INTENT_SIGNAL_SETTING[source]
  ];
}

/**
 * Records an HSA intent signal from an element that stays on the storefront
 * (the LMN flow today; the PDP and cart widgets next). Unlike the buy-button
 * paths nothing navigates afterwards, so the write is idempotent against the
 * cart's current state: it is skipped when the cart already carries priority
 * with this exact signal, a live one-shot exclusivity is preserved in the
 * intentAttribute rather than contradicted, and a failure is logged, never thrown into
 * the caller's UI.
 */
export async function recordHsaIntentSignal(
  client: HsaIntentCartClient,
  signal: { source: HsaIntentSource; exclusive: boolean },
): Promise<void> {
  try {
    const cart = await client.cartGet();
    const attributes = cart.attributes ?? {};
    const exclusive =
      signal.exclusive ||
      attributes[REDO_HSA_PAYMENT_EXCLUSIVITY_ATTRIBUTE_KEY] === "true";
    const recorded = safeParseHsaIntentCartAttribute(
      attributes[REDO_HSA_INTENT_ATTRIBUTE_KEY],
    );
    if (
      attributes[REDO_HSA_PAYMENT_PRIORITY_ATTRIBUTE_KEY] === "true" &&
      recorded?.source === signal.source &&
      recorded.exclusive === exclusive
    ) {
      return;
    }
    log("hsa: recording intent signal", { source: signal.source, exclusive });
    await client.updateCartAttribute({
      attributes: buildHsaIntentCartAttributes({
        source: signal.source,
        exclusive,
      }),
    });
    void reportHsaIntentSignal({
      source: signal.source,
      exclusive,
      cartToken: cart.token,
    });
  } catch (error) {
    datadogLog("error", "hsa: failed to record intent signal", {
      source: signal.source,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Wraps a questionnaire driver so finishing a COMPLETE session also records
 * the signal (awaited, so the attribute is on the cart before the shopper can
 * head to checkout). A disqualification finishes without recording anything.
 */
export function withLmnCompletionSignal(
  driver: HsaSessionDriver,
  record: () => Promise<void>,
): HsaSessionDriver {
  return {
    ...driver,
    async finish(input) {
      const result = await driver.finish(input);
      if (input.state.status === HsaSessionStatus.COMPLETE) {
        await record();
      }
      return result;
    },
  };
}
